import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import AuthModal from '../auth/AuthModal';
import NotificationDropdown from '../notifications/NotificationDropdown';
import styles from './Header.module.css';

export default function Header() {
    const { language, toggleLanguage, t, getNextLanguageName } = useLanguage();
    const { user, isLoggedIn, logout } = useAuth();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const location = useLocation();

    // 是否显示传奇功能（仅在开发环境或设置了环境变量时显示）
    const showLegendFeatures = import.meta.env.DEV || import.meta.env.VITE_SHOW_LEGEND_FEATURES === 'true';

    const navItems = [
        { path: '/', label: t('nav.configurator') || 'Configurator' },
        { path: '/3d-preview', label: t('nav.preview3d') || '3D Preview' },
        { path: '/gallery', label: t('nav.gallery') || 'Gallery' },
        // 传奇功能仅在开发环境显示
        ...(showLegendFeatures ? [
            { path: '/legends', label: t('nav.legends') || '传奇画廊' },
            { path: '/hall-of-fame', label: t('nav.hallOfFame') || '传奇殿堂' },
        ] : []),
    ];

    const handleLogout = async () => {
        await logout();
        setShowUserMenu(false);
    };

    return (
        <>
            <header className={styles.header}>
                <Link to="/" className={styles.logo}>
                    HARLEY-DAVIDSON <span className={styles.sub}>FAT BOY</span>
                </Link>

                <nav className={styles.nav}>
                    {navItems.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`${styles.navLink} ${location.pathname === item.path ? styles.active : ''}`}
                        >
                            {item.label}
                        </Link>
                    ))}
                </nav>

                <div className={styles.actions}>
                    {/* 移动端汉堡菜单按钮 */}
                    <button
                        className={styles.mobileMenuBtn}
                        onClick={() => setShowMobileMenu(!showMobileMenu)}
                        aria-label="Toggle menu"
                    >
                        <span className={`${styles.menuIcon} ${showMobileMenu ? styles.menuIconOpen : ''}`} />
                    </button>

                    <button className={styles.langBtn} onClick={toggleLanguage}>
                        {getNextLanguageName()}
                    </button>

                    {isLoggedIn && <NotificationDropdown />}

                    {isLoggedIn ? (
                        <div className={styles.userWrapper}>
                            <button
                                className={styles.userBtn}
                                onClick={() => setShowUserMenu(!showUserMenu)}
                            >
                                <span className={styles.avatar}>
                                    {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                                <span className={styles.userName}>{user?.nickname}</span>
                            </button>

                            {showUserMenu && (
                                <div className={styles.userMenu}>
                                    <Link
                                        to="/profile"
                                        className={styles.menuItem}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        {t('nav.profile') || 'My Profile'}
                                    </Link>
                                    {showLegendFeatures && (
                                        <Link
                                            to={`/rider/${user?.id}`}
                                            className={`${styles.menuItem} ${styles.legendsLink}`}
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            🔥 {{ zh: '我的传奇', en: 'My Legends', de: 'Meine Legenden' }[language] || 'My Legends'}
                                        </Link>
                                    )}
                                    <Link
                                        to="/gallery/upload"
                                        className={styles.menuItem}
                                        onClick={() => setShowUserMenu(false)}
                                    >
                                        {t('nav.upload') || 'Upload'}
                                    </Link>
                                    {user?.role === 'admin' && (
                                        <Link
                                            to="/admin"
                                            className={`${styles.menuItem} ${styles.adminLink}`}
                                            onClick={() => setShowUserMenu(false)}
                                        >
                                            {t('nav.admin') || 'Admin Panel'}
                                        </Link>
                                    )}
                                    <button
                                        className={styles.menuItem}
                                        onClick={handleLogout}
                                    >
                                        {t('nav.logout') || 'Logout'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <button
                            className={styles.loginBtn}
                            onClick={() => setShowAuthModal(true)}
                        >
                            {t('nav.login') || 'Login'}
                        </button>
                    )}
                </div>
            </header>

            {/* 移动端导航菜单 */}
            <nav className={`${styles.mobileNav} ${showMobileMenu ? styles.open : ''}`}>
                {navItems.map(item => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`${styles.mobileNavLink} ${location.pathname === item.path ? styles.active : ''}`}
                        onClick={() => setShowMobileMenu(false)}
                    >
                        {item.label}
                    </Link>
                ))}
            </nav>

            <AuthModal
                isOpen={showAuthModal}
                onClose={() => setShowAuthModal(false)}
            />
        </>
    );
}
